import type {
  OnboardingStatus,
  OnboardingTask,
} from 'src/entities/OnboardingStatus';

import { STUDIO_CHECKLIST_TASK_CODE_BY_ID } from './studioChecklist';

/**
 * Checklist 1 of the onboarding-activation Getting started card, client
 * activation first. Order here is the order the rows render in.
 */
export const CHECKLIST_ONE_STEP_IDS = ['explorePortal', 'publishApp'] as const;

/** Checklist 2, "Set up your workspace". Its steps can be done in any order. */
export const CHECKLIST_TWO_STEP_IDS = [
  'branding',
  'anotherApp',
  'contacts',
] as const;

export type ChecklistOneStepId = (typeof CHECKLIST_ONE_STEP_IDS)[number];
export type ChecklistTwoStepId = (typeof CHECKLIST_TWO_STEP_IDS)[number];
export type ActivationStepId = ChecklistOneStepId | ChecklistTwoStepId;

export type ActivationStepCompletion = Record<ActivationStepId, boolean>;

/**
 * Which part of the card shows: checklist 1, the "You're on a roll" card that
 * unlocks checklist 2, or checklist 2.
 */
export type ActivationStage = 'checklistOne' | 'unlock' | 'checklistTwo';

/** Values for the guided-tour param that open checklist 2's guided modals. */
export const GUIDED_TOUR_ANOTHER_APP = 'anotherApp';
export const GUIDED_TOUR_FIRST_CONTACT = 'firstContact';

// Plain strings: anotherApp and firstContact are not in the classic TaskCodes
// enum, which the classic onboarding card switches over exhaustively.
const ACTIVATION_TASK_CODE_BY_ID: Record<ActivationStepId, string> = {
  explorePortal: STUDIO_CHECKLIST_TASK_CODE_BY_ID.explorePortal,
  publishApp: STUDIO_CHECKLIST_TASK_CODE_BY_ID.publishApp,
  branding: 'customization',
  anotherApp: 'anotherApp',
  contacts: 'firstContact',
};

// The task shape with a plain string code, so the API's onboarding tasks fit it
// and so do the codes the classic enum leaves out.
export type ActivationTask = Pick<OnboardingTask, 'done' | 'status'> & {
  taskCode: string;
};

// "Do this later" checks a step off just like doing it.
const isTaskComplete = (task: ActivationTask | undefined): boolean =>
  task?.done === true || task?.status === 'done' || task?.status === 'skipped';

/**
 * Whether the response carries the onboarding-activation checklist. The backend
 * only sends checklist 2 to Studio workspaces with the flag on, so a response
 * without it (classic, flag off) keeps today's two-task card.
 */
export const hasActivationTasks = (
  tasks: readonly ActivationTask[] | undefined,
): boolean =>
  (tasks ?? []).some(
    (task) => task.taskCode === ACTIVATION_TASK_CODE_BY_ID.anotherApp,
  );

export const getActivationStepCompletion = (
  tasks: readonly ActivationTask[] | undefined,
): ActivationStepCompletion => {
  const taskByCode = new Map(
    (tasks ?? []).map((task) => [task.taskCode, task]),
  );
  const isComplete = (id: ActivationStepId) =>
    isTaskComplete(taskByCode.get(ACTIVATION_TASK_CODE_BY_ID[id]));
  // Spelled out so a new step can't silently go unread.
  return {
    explorePortal: isComplete('explorePortal'),
    publishApp: isComplete('publishApp'),
    branding: isComplete('branding'),
    anotherApp: isComplete('anotherApp'),
    contacts: isComplete('contacts'),
  };
};

export const countCompletedSteps = (
  stepIds: readonly ActivationStepId[],
  completion: ActivationStepCompletion,
): number => stepIds.filter((id) => completion[id]).length;

export const areStepsCompleted = (
  stepIds: readonly ActivationStepId[],
  completion: ActivationStepCompletion,
): boolean => stepIds.every((id) => completion[id]);

/** Whether both checklists are done, the state that hides the card on next load. */
export const areActivationStepsCompleted = (
  completion: ActivationStepCompletion,
): boolean =>
  areStepsCompleted(CHECKLIST_ONE_STEP_IDS, completion) &&
  areStepsCompleted(CHECKLIST_TWO_STEP_IDS, completion);

/**
 * The checklist the owner is working through, as the client preview card counts it:
 * checklist 1 until it's done, then checklist 2. The backend's preview summary
 * counts the same way.
 */
export const getCurrentChecklistSteps = (
  completion: ActivationStepCompletion,
): { completed: number; total: number } => {
  const stepIds = areStepsCompleted(CHECKLIST_ONE_STEP_IDS, completion)
    ? CHECKLIST_TWO_STEP_IDS
    : CHECKLIST_ONE_STEP_IDS;
  return {
    completed: countCompletedSteps(stepIds, completion),
    total: stepIds.length,
  };
};

export const getActivationStage = (
  completion: ActivationStepCompletion,
  seenAt: OnboardingStatus['fields']['seenAt'],
): ActivationStage => {
  if (!areStepsCompleted(CHECKLIST_ONE_STEP_IDS, completion)) {
    return 'checklistOne';
  }
  // "Keep going" records part2Unlock, so a reload doesn't bring the card back.
  return seenAt?.part2Unlock ? 'checklistTwo' : 'unlock';
};
